// The cards on the phone's top strip, ranked. One card shows at a time, so the
// order is the whole design: what this person would want to glance at in the
// middle of a call comes first.
//
//   1. The host's "say next" line, then a private question Breeze suggested for
//      this person.
//   2. The next question nobody has asked yet (shared).
//   3. The newest shared note or open next step, including ones Breeze added.
//
// A card that only this person may see (the say-next line and suggested
// questions) is never produced while they share their screen, the same rule the
// full panel keeps by collapsing Mine and Host.

import type { SayNext } from "./assistant.js";
import { BREEZE_ID } from "./assistant.ts";
import type { PanelItem, PanelState, PrivateData, SuggestionAudience, SuggestionItem } from "./panel.js";

export type StripKind = "say" | "suggested" | "question" | "note" | "step";

export interface StripCard {
  /** Stable for the same content, so a changed list can keep the selected card. */
  readonly id: string;
  readonly kind: StripKind;
  readonly text: string;
  /** Only this person may see it: never shown while sharing the screen. */
  readonly private: boolean;
  readonly byBreeze: boolean;
  /** Author name for a shared card written by a person. */
  readonly by: string;
  /** Next steps: who it is for. */
  readonly owner: string;
}

export interface StripInput {
  readonly isHost: boolean;
  readonly sharingScreen: boolean;
  readonly shared: PanelState | null;
  readonly priv: PrivateData;
  readonly suggestions: readonly { readonly audience: SuggestionAudience; readonly items: readonly SuggestionItem[] }[];
  /** The assistant's say-next line; only ever non-null on a host's socket. */
  readonly say: SayNext | null;
}

export const STRIP_MAX_CARDS = 8;

function sharedCard(kind: "question" | "note" | "step", item: PanelItem): StripCard {
  return {
    id: `${kind}:${item.id}`,
    kind,
    text: item.text,
    private: false,
    byBreeze: item.authorId === BREEZE_ID,
    by: item.authorId === BREEZE_ID ? "" : item.authorName,
    owner: kind === "step" ? (item.owner ?? "") : "",
  };
}

export function stripCards(input: StripInput, max: number = STRIP_MAX_CARDS): StripCard[] {
  const out: StripCard[] = [];
  const privateOk = !input.sharingScreen;

  if (privateOk && input.isHost && input.say && input.say.line.trim()) {
    out.push({ id: `say:${input.say.line}`, kind: "say", text: input.say.line.trim(), private: true, byBreeze: false, by: "", owner: "" });
  }

  const have = new Set([
    ...input.priv.questions.map((q) => q.text.trim()),
    ...(input.shared?.questions ?? []).map((q) => q.text.trim()),
  ]);
  if (privateOk) {
    const seen = new Set<string>();
    for (const batch of input.suggestions) {
      if (batch.audience !== "self") continue;
      for (const item of batch.items) {
        const text = item.text.trim();
        if (item.kind !== "question" || !text || have.has(text) || seen.has(item.id)) continue;
        seen.add(item.id);
        out.push({ id: `suggested:${item.id}`, kind: "suggested", text, private: true, byBreeze: false, by: "", owner: "" });
      }
    }
  }

  const shared = input.shared;
  if (shared) {
    const open = shared.questions.filter((q) => !q.asked).sort((a, b) => a.createdAt - b.createdAt);
    for (const q of open) out.push(sharedCard("question", q));

    const recent: { readonly at: number; readonly card: StripCard }[] = [
      ...shared.notes.map((n) => ({ at: n.createdAt, card: sharedCard("note", n) })),
      ...shared.steps.filter((s) => !s.done).map((s) => ({ at: s.createdAt, card: sharedCard("step", s) })),
    ];
    recent.sort((a, b) => b.at - a.at);
    for (const r of recent) out.push(r.card);
  }

  return out.slice(0, Math.max(0, max));
}
